import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import Modal from '@/components/modal/Modal.tsx';
import ModalHeader from '@/components/modal/ModalHeader.tsx';
import ModalContent from '@/components/modal/ModalContent.tsx';
import ModalFooter from '@/components/modal/ModalFooter.tsx';
import ActionButton from '@/components/buttons/ActionButton.tsx';
import useCreateFinancialTransaction from '@/features/dashboard/financialTransactions/hooks/useCreateFinancialTransaction.ts';
import {
  financialTransactionSchema,
  FinancialTransactionFormData,
  FinancialTransactionInputData,
} from '@/features/dashboard/financialTransactions/schemas/financialTransactionSchema.ts';
import dayjs from 'dayjs';
import { Currency } from '@/features/dashboard/paymentConfig/types/Currency.ts';
import FinancialTransactionForm from '@/features/dashboard/financialTransactions/components/FinancialTransactionForm.tsx';
import { scrollToAllocationsError } from '@/features/dashboard/financialTransactions/components/allocationsError.ts';
import { TransactionAllocationType } from '@/features/dashboard/financialTransactions/types/TransactionAllocation.ts';
import { TransactionDirection } from '@/features/dashboard/financialTransactions/types/TransactionDirection.ts';
import { PaymentMethod } from '@/features/dashboard/tourConfig/types/PaymentMethod.ts';

interface CreateFinancialTransactionModalProps {
  onClose: () => void;
  clientId?: string;
}

const CreateFinancialTransactionModal = ({
  onClose,
  clientId,
}: CreateFinancialTransactionModalProps) => {
  const createMutation = useCreateFinancialTransaction();

  const methods = useForm<FinancialTransactionInputData>({
    resolver: zodResolver(financialTransactionSchema),
    defaultValues: {
      direction: TransactionDirection.INCOMING,
      clientId: clientId ?? '',
      amount: undefined,
      currency: Currency.PLN,
      paymentMethod: PaymentMethod.TRANSFER,
      transactionDate: new Date(),
      transactionTime: dayjs().format('HH:mm'),
      description: '',
      allocations: [
        {
          type: TransactionAllocationType.BOOKING,
          bookingId: '',
          expensePlannedTransactionId: '',
          amount: undefined,
        },
      ],
    },
  });

  const { handleSubmit } = methods;

  const onSubmit = (data: FinancialTransactionFormData) => {
    createMutation.mutate(data, { onSuccess: () => onClose() });
  };

  return (
    <Modal onClose={onClose} isDirty={methods.formState.isDirty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, scrollToAllocationsError)}>
          <ModalHeader title="Dodaj transakcję" onClose={onClose} />
          <ModalContent>
            <FinancialTransactionForm mode="create" />
          </ModalContent>
          <ModalFooter>
            <ActionButton
              tone="ghost"
              size="sm"
              type="button"
              onClick={onClose}
            >
              Anuluj
            </ActionButton>
            <ActionButton
              tone="accent"
              size="sm"
              type="submit"
              isLoading={createMutation.isPending}
            >
              Dodaj transakcję
            </ActionButton>
          </ModalFooter>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default CreateFinancialTransactionModal;
